<template>
    <div class="webcam-feed"
         :style="{width: width + 'px'}">
        <video ref="video-el"
               :width="width"
               :height="height"></video>
    </div>
</template>

<script>
export class WebcamUnsupportedError extends Error {}

/**
 * @param {MediaStream} stream
 */
const stopStream = (stream) => {
    stream.getTracks().forEach((track) => {
        track.stop();
    });
};

export default {
    props: {
        width: {
            type: Number,
            required: true,
        },
        height: {
            type: Number,
            required: true,
        },
    },

    emits: [
        'error',
    ],

    data() {
        return {
            canvas: null,
            ratio: this.width / this.height,
            stream: null,
            supported: false,
            isUnmounted: false,
        };
    },

    computed: {
        /**
         * @return {HTMLVideoElement}
         */
        videoElement() {
            return this.$refs['video-el'];
        },
    },

    mounted() {
        this.initializeCamera();
    },

    beforeUnmount() {
        this.isUnmounted = true;

        if (this.stream) {
            stopStream(this.stream);
        }
    },

    methods: {
        initializeCamera() {
            this.canvas = document.createElement('canvas');

            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                this.supported = true;
            }

            // Some browsers support getUserMedia, so check for availability first.
            // It should be available on iOS since 11.3, but this code was copied
            // from WebcamService which was introduced before then.
            if (this.supported) {
                navigator.mediaDevices.getUserMedia({ video: true, audio: false })
                    .then((stream) => {
                        if (this.isUnmounted) {
                            // The component was unmounted before the promise returned.
                            // Stop the stream that just started.
                            stopStream(stream);
                            return;
                        }

                        this.stream = stream;
                        this.videoElement.srcObject = stream;

                        setTimeout(() => {
                            this.videoElement.play();
                        }, 1);
                    })
                    .catch((err) => {
                        console.error(err);
                        this.$emit('error', err);
                    });
            } else {
                this.$emit('error', new WebcamUnsupportedError('This browser does not support capturing photos.'));
            }

            this.videoElement.addEventListener(
                'canplay',
                () => {
                    if (!this.streaming) {
                        this.canvas.setAttribute('width', this.width);
                        this.canvas.setAttribute('height', this.height);
                        this.streaming = true;
                    }
                },
                false
            );
        },

        /**
         * @return {string}
         */
        getPhoto() {
            if (this.streaming && this.width && this.height) {
                let context = this.canvas.getContext('2d');
                context.drawImage(this.videoElement, 0, 0, this.width, this.height);

                return this.canvas.toDataURL('image/jpeg', 0.65);
            }

            throw new Error('Unable to getPhoto because camera is not initialized.');
        },
    },
};
</script>

<style lang="less">
.webcam-feed {
    &,
    video {
        max-width: 100%;
        display: block;
    }
}
</style>
